import { CSSProperties } from 'react';
import { COLORS, FONTS } from '../../globalStyles';

const EASE = 'cubic-bezier(0.65, 0, 0.35, 1)';

// The mark is RENDERED at its full intro size so it's never scaled UP — scaling an
// SVG up rasterizes it and looks pixelated on iOS (Safari rasterizes at the small
// backing size, then blows up the bitmap). It sits at scale 1 during the intro,
// then scales DOWN to the 36px header size when docking; downscaling stays crisp.
export const INTRO_MARK_SIZE = 234; // intro display size (matches the old 36 * 6.5)
const INTRO_HALF = INTRO_MARK_SIZE / 2; // 117px — half the box, for centering
const DOCK_SCALE = 36 / INTRO_MARK_SIZE; // ≈ 0.1538 — shrink to the 36px header mark

export const styles: Record<string, CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 1000,
    background: COLORS.bg,
    transition: 'opacity 0.28s ease',
  },
  // The mark scales/moves via a single transform. The wordmark is a separate
  // sibling (not a child) so the two can animate to the header independently —
  // the logo docks first, the wordmark follows a beat later.
  markBox: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: `${INTRO_MARK_SIZE}px`,
    height: `${INTRO_MARK_SIZE}px`,
    transformOrigin: 'top left',
    willChange: 'transform',
    transition: `transform 0.55s ${EASE}`,
  },
  markBoxIntro: {
    transform: `translate(calc(50vw - ${INTRO_HALF}px), calc(44vh - ${INTRO_HALF}px)) scale(1)`,
  },
  markBoxDocked: {
    transform: `translate(var(--header-pad-x), var(--header-pad-y)) scale(${DOCK_SCALE})`,
  },
  mark: {
    transition: 'opacity 0.42s ease',
  },
  wordmark: {
    position: 'fixed',
    top: 0,
    left: 0,
    whiteSpace: 'nowrap',
    fontFamily: FONTS.body,
    fontWeight: 500,
    // Constant size — the old 18.5px → 1.25rem change was imperceptible, and animating
    // font-size forces a text reflow every frame (janky on mobile).
    fontSize: '1.25rem',
    textTransform: 'uppercase',
    color: COLORS.text,
    willChange: 'transform',
    // Position animates via transform (GPU); only letter-spacing still tweens — its
    // "wide → tight" flourish is the one effect that needs a reflow, now isolated to
    // this small promoted layer instead of also re-laying-out on top/left/font-size.
    transition: `opacity 0.42s ease, transform 0.55s ${EASE}, letter-spacing 0.55s ${EASE}`,
  },
  // Centered under the diamond. -50% centers on 50vw; +0.413em (half the letter-spacing)
  // counters the trailing-space shift.
  wordmarkIntro: {
    transform: 'translate(calc(50vw - 50% + 0.413em), calc(44vh + 80px))',
    letterSpacing: '0.826em',
  },
  // Beside the docked mark, vertically centered — matches the header logo
  // (mark 36px at the shared --header-pad inset, gap 0.7rem). -50% centers on the Y anchor.
  wordmarkDocked: {
    transform:
      'translate(calc(var(--header-pad-x) + 36px + 0.7rem), calc(var(--header-pad-y) + 18px - 50%))',
    letterSpacing: '0.26em',
  },
};
